import { APP_ID, InjectionToken, NgModule, TransferState, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule, provideClientHydration, withNoHttpTransferCache } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UrlSerializer } from '@angular/router';
import { DotCMSEditablePageService, provideDotCMSImageLoader } from '@dotcms/angular';
import { DotCMSClientConfig } from '@dotcms/types';
import { firstValueFrom, tap } from 'rxjs';

import { COOKIE_CONSENT_VERSION } from 'ish-core/configurations/state-keys';
import { CoreModule } from 'ish-core/core.module';
import { PWAUrlSerializer } from 'ish-core/routing/pwa-url.serializer';
import { ConfigService } from 'ish-core/services/dotcms-uve/dot-cms-config.service';

import { environment } from '../environments/environment';

import { AppComponent } from './app.component';
import { CompareRoutingModule } from './extensions/compare/pages/compare-routing.module';
import { ContactUsRoutingModule } from './extensions/contact-us/pages/contact-us-routing.module';
import { CopilotExportsModule } from './extensions/copilot/exports/copilot-exports.module';
import { CustomQuickorderRoutingModule } from './extensions/custom-quickorder/pages/custom-quickorder-routing.module';
import { DotCmsUveRoutingModule } from './extensions/dot-cms-uve/pages/dot-cms-uve-routing.module';
import { EmbedGPTRoutingModule } from './extensions/embed-gpt/pages/embed-gpt-routing.module';
import { MultipleShippingCheckoutRoutingModule } from './extensions/multiple-shipping-checkout/pages/multiple-shipping-checkout-routing.module';
import { OnePageCheckoutRoutingModule } from './extensions/one-page-checkout/pages/one-page-checkout-routing.module';
import { PunchoutRoutingModule } from './extensions/punchout/pages/punchout-routing.module';
import { QuickorderRoutingModule } from './extensions/quickorder/pages/quickorder-routing.module';
import { QuotingRoutingModule } from './extensions/quoting/pages/quoting-routing.module';
import { RecentlyRoutingModule } from './extensions/recently/pages/recently-routing.module';
import { SalesRepRoutingModule } from './extensions/sales-rep/pages/sales-rep-routing.module';
import { StoreLocatorRoutingModule } from './extensions/store-locator/pages/store-locator-routing.module';
import { SubscriptionRoutingModule } from './extensions/subscription/pages/subscription-routing.module';
import { TactonRoutingModule } from './extensions/tacton/pages/tacton-routing.module';
import { WishlistSharingRoutingModule } from './extensions/wishlists/pages/wishlist-sharing-routing.module';
import { AppLastRoutingModule } from './pages/app-last-routing.module';
import { AppRoutingModule } from './pages/app-routing.module';
import { ShellModule } from './shell/shell.module';

export const CONFIG_READY = new InjectionToken<Promise<void>>('CONFIG_READY');

export function initializeApp(configService: ConfigService) {
  return async () => {
    try {
      const config = await firstValueFrom(configService.loadConfig());
      configService.setConfig(config);
    } catch (error) {
      throw error;
    }
  };
}
@NgModule({
  declarations: [AppComponent],
  /* eslint-disable @angular-eslint/sort-ngmodule-metadata-arrays */
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    ShellModule,
    CopilotExportsModule,
    AppRoutingModule,
    QuickorderRoutingModule,
    QuotingRoutingModule,
    PunchoutRoutingModule,
    TactonRoutingModule,
    StoreLocatorRoutingModule,
    RecentlyRoutingModule,
    CompareRoutingModule,
    ContactUsRoutingModule,
    CustomQuickorderRoutingModule,
    OnePageCheckoutRoutingModule,
    SubscriptionRoutingModule,
    MultipleShippingCheckoutRoutingModule,
    SalesRepRoutingModule,
    //DotCmsRoutingModule,
    EmbedGPTRoutingModule,
    WishlistSharingRoutingModule,
    DotCmsUveRoutingModule,
    AppLastRoutingModule,
  ],
  /* eslint-enable @angular-eslint/sort-ngmodule-metadata-arrays */
  bootstrap: [AppComponent],
  providers: [
    { provide: UrlSerializer, useClass: PWAUrlSerializer },
    { provide: APP_ID, useValue: 'intershop-pwa' },
    provideClientHydration(withNoHttpTransferCache()),
    provideAppInitializer(() => {
      const configService = inject(ConfigService);
      return firstValueFrom(
        configService.loadConfig().pipe(tap((config: DotCMSClientConfig) => configService.setConfig(config)))
      );
    }),
    provideDotCMSImageLoader(environment.dotCmsApiEndpoint),
    DotCMSEditablePageService,
  ],
})
export class AppModule {
  constructor(transferState: TransferState) {
    if (!transferState.hasKey<number>(COOKIE_CONSENT_VERSION)) {
      transferState.set(COOKIE_CONSENT_VERSION, environment.cookieConsentVersion);
    }
  }
}
